import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";
import { formatErr } from "@/lib/api";

// Remembers the last-used email address so it's pre-filled the next time
// someone opens the login screen (e.g. after signing out). We deliberately
// only remember the email here, in plain localStorage — never the password.
//
// The password itself is left to the browser's own built-in password
// manager (Chrome/Safari/Firefox all show a native "Save password?" prompt
// after a successful sign-in on a form like this one, as long as the
// email/password inputs have the right autoComplete hints, which they do
// below). That's more secure than storing a raw password ourselves, and it
// also means saved passwords sync with the user's browser/account the way
// they expect from "every other web app".
const REMEMBERED_EMAIL_KEY = "sfc_remembered_email";

function rememberEmail(email) {
  try {
    localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
  } catch {
    // Best-effort only — never block login on this.
  }
}

function getRememberedEmail() {
  try {
    return localStorage.getItem(REMEMBERED_EMAIL_KEY) || "";
  } catch {
    return "";
  }
}

export default function Login() {
  const { user, loading: authLoading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  // Pre-fill the email from last time, so returning users only need to
  // (re)type their password — the browser's own password manager takes care
  // of filling that in automatically once it has saved it once.
  useEffect(() => {
    const rememberedEmail = getRememberedEmail();
    if (rememberedEmail) setEmail(rememberedEmail);
  }, []);

  // If someone is already signed in and lands back on /login (e.g. by
  // pressing the phone's back button), send them straight to the dashboard
  // instead of showing the login form again.
  if (!authLoading && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      rememberEmail(email);
      toast.success("Welcome back");
      // replace (not push) so /login doesn't stay in history — otherwise
      // pressing the back button after logging in lands back on the login
      // screen instead of leaving the app.
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm border border-gray-200 rounded-xl shadow-sm p-8 space-y-7 bg-white"
        data-testid="login-form"
      >
        <div className="text-center space-y-1">
          <div className="flex items-center justify-center gap-2">
            <img src="/sfc-logo.png" alt="Sandeep Forms Centre" className="w-8 h-8 object-contain" />
            <div className="font-display font-bold text-2xl tracking-tight text-gray-900">SFC Go</div>
          </div>
          <h2 className="font-display font-bold text-2xl text-gray-900">Sign in</h2>
          <p className="text-sm text-gray-500">Access your delivery command center.</p>
        </div>
        <div className="space-y-4">
          <div>
            <label htmlFor="login-email" className="text-xs uppercase tracking-widest text-gray-500">Email</label>
            <input
              type="email"
              name="email"
              id="login-email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="w-full mt-1.5 h-11 px-3 rounded-md border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-signal/40"
              required
            />
          </div>
          <div>
            <label htmlFor="login-password" className="text-xs uppercase tracking-widest text-gray-500">Password</label>
            <input
              type="password"
              name="password"
              id="login-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full mt-1.5 h-11 px-3 rounded-md border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-signal/40"
              required
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit-button"
          className="w-full h-11 signal-btn rounded-md font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? "Signing in..." : "Continue"} <ArrowRight size={16} weight="bold" />
        </button>
      </form>
    </div>
  );
}
